import React, { useState } from "react";
import {
  Box,
  TextField,
  Typography,
  Button,
  IconButton,
  InputAdornment,
  ToggleButton,
  ToggleButtonGroup,
  Divider,
} from "@mui/material";
import PersonIcon from "@mui/icons-material/Person";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import AppSnackbar from "../Components/Snackbar/snackbar";
import { useNavigate } from "react-router-dom";
import apiEndpoints from "../apiconfig";

export default function LoginPage() {
  const [showPassword, setShowPassword] = useState(false);
  const [role, setRole] = useState("admin");
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "",
  });

  const [form, setForm] = useState({ username: "", password: "" });
  const [errors, setErrors] = useState({ username: "", password: "" });

  const navigate = useNavigate();

  const handleRoleChange = (_, newRole) => {
    if (newRole) setRole(newRole);
  };

  const validate = () => {
    let temp = {};
    temp.username = form.username ? "" : "Username is required";
    temp.password = form.password ? "" : "Password is required";
    setErrors(temp);
    return Object.values(temp).every((x) => x === "");
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    if (!validate()) {
      setSnackbar({
        open: true,
        message: "Please fill all required fields",
        severity: "error",
      });
      return;
    }

    try {
      const formData = new FormData();
      formData.append("login_type", role);       
      formData.append("login_id", form.username);  
      formData.append("password", form.password);

      const res = await fetch(
        `${apiEndpoints.register}?action=login`,
        {
          method: "POST",
          body: formData,
        }
      );

      const data = await res.json();

      if (data.status === "success") {
        setSnackbar({
          open: true,
          message: data.message,
          severity: "success",
        });

        // save token & user in browser local storage
        localStorage.setItem("token", data.data.token);
        localStorage.setItem("role", data.data.role);
        localStorage.setItem("user", JSON.stringify(data.data.user));

        // redirect based on role
        setTimeout(() => {
          navigate(role === "admin" ? "/admin" : "/teacher");
        }, 1000);
      } else {
        setSnackbar({
          open: true,
          message: data.message || "Login failed",
          severity: "error",
        });
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: "Server error. Please try again!",
        severity: "error",
      });
    }
  };

  const handleChange = (field, value) => {
    setForm({ ...form, [field]: value });
    setErrors({ ...errors, [field]: "" });
  };

  return (
    <Box
      sx={{
        width: "100vw",
        height: "100vh",
        bgcolor: "#F2F4FF",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        p: { xs: 1, md: 3 },
      }}
    >
      {/* OUTER CARD CONTAINER */}
      <Box
        sx={{
          width: { xs: "100%", sm: "90%", md: "70%", lg: "65%" },
          display: "flex",
          bgcolor: "#FFFFFF",
          borderRadius: "28px",
          boxShadow: "0 10px 40px rgba(63, 81, 181, 0.25)",
          overflow: "hidden",
          minHeight: "72vh",
        }}
      >
        {/* LEFT ILLUSTRATION */}
        <Box
          sx={{
            width: { xs: "0%", md: "50%" },
            display: { xs: "none", md: "flex" },
            justifyContent: "center",
            alignItems: "center",
            bgcolor: "#EEF0FF",
            borderRight: "1px solid #E0E3FF",
            p: 3,
          }}
        >
          <img
            src="/LoginImage.jpg"
            alt="login illustration"
            style={{ width: "90%", borderRadius: "16px" }}
          />
        </Box>

        {/* RIGHT LOGIN FORM */}
        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{
            width: { xs: "100%", md: "50%" },
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            px: { xs: 4, sm: 6, md: 6 },
            py: { xs: 3, md: 6 },
          }}
        >
          {/* TITLE */}
          <Typography
            sx={{
              fontSize: 36,
              fontWeight: 700,
              mb: 3,
              color: "#3F51B5",
              textAlign: "center",
            }}
          >
            Login
          </Typography>

          {/* ROLE TOGGLE */}
          <Box sx={{ display: "flex", justifyContent: "center", mb: 4 }}>
            <ToggleButtonGroup
              exclusive
              value={role}
              onChange={handleRoleChange}
              sx={{
                backgroundColor: "#F0F3FF",
                borderRadius: "40px",
                p: 0.5,
                boxShadow: "inset 0 0 8px rgba(0, 0, 0, 0.08)",
                "& .MuiToggleButton-root": {
                  border: 0,
                  borderRadius: "30px !important",
                  px: 4,
                  py: 1.2,
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: 15,
                  color: "#3F51B5",
                  transition: "all 0.25s ease",
                },
                "& .Mui-selected": {
                  background: "linear-gradient(90deg, #3F51B5 0%, #536DFE 90%)",
                  color: "#fff !important",
                  boxShadow: "0 4px 12px rgba(63, 81, 181, 0.45)",
                },
                "& .MuiToggleButton-root:hover": {
                  backgroundColor: "rgba(63, 81, 181, 0.06)",
                },
              }}
            >
              <ToggleButton value="admin">Admin</ToggleButton>
              <ToggleButton value="staff">Staff</ToggleButton>
            </ToggleButtonGroup>
          </Box>

          {/* USERNAME */}
          <TextField
            label="Email/Mobile No"
            fullWidth
            value={form.username}
            onChange={(e) => handleChange("username", e.target.value)}
            error={!!errors.username}
            helperText={errors.username}
            sx={{
              mb: 2,
              "& fieldset": { borderRadius: "14px" },
              "& input": { padding: "14px" },
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <PersonIcon />
                </InputAdornment>
              ),
            }}
          />

          {/* PASSWORD */}
          <TextField
            label="Password"
            type={showPassword ? "text" : "password"}
            fullWidth
            value={form.password}
            onChange={(e) => handleChange("password", e.target.value)}
            error={!!errors.password}
            helperText={errors.password}
            sx={{
              mb: 2,
              "& fieldset": { borderRadius: "14px" },
              "& input": { padding: "14px" },
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowPassword(!showPassword)}>
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          {/* FORGOT PASSWORD */}
          {/* {role === "admin" ? (
            <Typography
              sx={{
                textAlign: "right",
                color: "#3F51B5",
                mb: 3,
                fontSize: 14,
                cursor: "pointer",
                "&:hover": { textDecoration: "underline" },
              }}
            >
              Forgot Password?
            </Typography>
          ) : (
            <Box sx={{ mb: 3, height: "20px" }} />
          )} */}

          <Button
            type="submit"
            fullWidth
            onClick={handleSubmit}
            sx={{
              py: 1.6,
              borderRadius: "30px",
              textTransform: "none",
              fontSize: 18,
              fontWeight: 700,
              background: "linear-gradient(90deg, #3F51B5 0%, #536DFE 90%)",
              color: "#fff",
            }}
          >
            Log In
          </Button>

          {/* SNACKBAR */}
          <AppSnackbar
            open={snackbar.open}
            message={snackbar.message}
            severity={snackbar.severity}
            onClose={() =>
              setSnackbar({
                open: false,
                message: "",
                severity: "",
              })
            }
          />
        </Box>
      </Box>
    </Box>
  );
}
